import type {
  ApiResponseSearchBar,
  CountryNavItem,
  CustomerNavigationBarApiResponse,
  CustomerNavigationBarComponent,
  EcommerceBarData,
  NavItem,
  NavigationNode,
  ProductSearchBar,
  SideNavigationComponent,
} from "@scott/ui-d2c/types";
import {
  fetchWithHeaders,
  getLinkItem,
  removeAllHTMLTags,
} from "@/utils/common.utils";
import { ANONYMOUS_USER_COMPONENTS_URL } from "@/utils/constants";
import { fetchNavigationComponents } from "./navigation.data";

export async function getHeaderItems(
  siteUid: string,
  country: string,
  lang: string,
  userCountry: string,
  browserLanguage?: string
) {
  const baseApiUrl = process.env.BASE_API_URL;
  if (!baseApiUrl) {
    throw new Error("BASE_API_URL is not set");
  }
  const [
    CountryNavItem,
    EcommerceBarData,
    CustomerNavigationBarComponent,
    navItems,
  ] = await Promise.all([
    fetchCountryNavigationBarData(
      siteUid,
      country,
      browserLanguage || lang,
      userCountry
    ),
    fetchEcommerceBarData(siteUid, country, lang),
    fetchCustomerNavBarData(siteUid, lang),
    fetchNavigationComponents(siteUid, lang),
  ]);
  const SideNavigationComponentData =
    navItems && CustomerNavigationBarComponent
      ? createSideNavBarComponentData(navItems, CustomerNavigationBarComponent)
      : null;
  return {
    CountryNavItem,
    EcommerceBarData,
    CustomerNavigationBarComponent,
    SideNavigationComponentData,
  };
}

export async function fetchCountryNavigationBarData(
  siteUid: string,
  _country: string,
  lang: string,
  userCountry: string
): Promise<CountryNavItem | null> {
  try {
    const baseApiUrl = process.env.BASE_API_URL;
    if (!baseApiUrl) {
      throw new Error(
        "BASE_API_URL is not set. Please configure the environment variable."
      );
    }

    const url = `${baseApiUrl}/${siteUid.split("_")[0]}_${userCountry.toLowerCase()}/${ANONYMOUS_USER_COMPONENTS_URL}/CountryNavigationBar?countryBar=${userCountry}&browserLanguage=${lang}`;
    const response = await fetchWithHeaders(url);
    const data = await response.json();
    if (!data?.navigationNode) {
      return null;
    }

    const navNode: NavigationNode = data.navigationNode;

    return extractCountryNavItem(userCountry, navNode);
  } catch (_error: unknown) {
    return null;
  }
}

export async function fetchCustomerNavBarData(siteUid: string, lang: string) {
  const url = `${process.env.BASE_API_URL}/${siteUid}/${ANONYMOUS_USER_COMPONENTS_URL}/CustomerNavigationBar?lang=${lang}`;
  try {
    const response = await fetchWithHeaders(url);

    if (!response.ok) {
      throw new Error(
        `Failed to fetch Customer navigation Bar data: ${response.status} ${response.statusText}`
      );
    }
    const apiData: CustomerNavigationBarApiResponse = await response.json();
    if (apiData) {
      return transformApiResponseToComponentData(siteUid, apiData);
    }
  } catch (_error) {
    return null;
  }
}

export async function fetchEcommerceBarData(
  siteUid: string,
  country: string,
  lang: string
): Promise<EcommerceBarData | null> {
  const url = `${process.env.BASE_API_URL}/${siteUid}/${ANONYMOUS_USER_COMPONENTS_URL}/headerTopBarComponent${country.toUpperCase()}?lang=${lang}`;
  try {
    const response = await fetchWithHeaders(url);
    const slotsData: EcommerceBarData = await response.json();
    if (!response.ok) {
      return null;
    }
    if (slotsData) {
      if (slotsData.leftText) {
        const match = getLinkItem(
          slotsData.leftText,
          slotsData.leftLink ? slotsData.leftLink : ""
        );
        if (match) {
          slotsData.leftText = match[2] ?? "";
          slotsData.leftLink = match[1] ?? "";
        }
      }
      if (slotsData.rightText) {
        const match = getLinkItem(
          slotsData.rightText,
          slotsData.rightLink ? slotsData.rightLink : ""
        );

        if (match) {
          slotsData.rightText = match[2] ?? "";
          slotsData.rightLink = match[1] ?? "";
        }
      }
      if (slotsData.centerText) {
        const match = getLinkItem(
          slotsData.centerText,
          slotsData.centerLink ? slotsData.centerLink : ""
        );

        if (match) {
          slotsData.centerText = match[2] ?? "";
          slotsData.centerLink = match[1] ?? "";
        }
      }
    }
    return slotsData;
  } catch (_error) {
    return null;
  }
}

const createSideNavBarComponentData = (
  navItems: NavItem[],
  customerNavBarComponent: CustomerNavigationBarComponent
): SideNavigationComponent => {
  const sideNavBarComponentData: SideNavigationComponent = {
    navItems,
    customerNavItems: [],
  };
  if (customerNavBarComponent.helpLink) {
    sideNavBarComponentData.customerNavItems?.push({
      text: customerNavBarComponent.helpLink.text,
      link: customerNavBarComponent.helpLink.link,
      external: true,
    });
  }
  if (customerNavBarComponent.dealerLink) {
    sideNavBarComponentData.customerNavItems?.push({
      text: customerNavBarComponent.dealerLink.text,
      link: customerNavBarComponent.dealerLink.link,
    });
  }
  if (customerNavBarComponent.bikeRegistrationLink) {
    sideNavBarComponentData.customerNavItems?.push({
      text: customerNavBarComponent.bikeRegistrationLink.text,
      link: customerNavBarComponent.bikeRegistrationLink.link,
    });
  }
  if (customerNavBarComponent.minicartLink) {
    sideNavBarComponentData.customerNavItems?.push({
      text: customerNavBarComponent.minicartLink.text,
      link: customerNavBarComponent.minicartLink.link,
    });
  }
  if (customerNavBarComponent.accountLink) {
    sideNavBarComponentData.customerNavItems?.push({
      text: customerNavBarComponent.accountLink.text,
      link: customerNavBarComponent.accountLink.link,
    });
  }
  return sideNavBarComponentData;
};

function extractCountryNavItem(
  country: string,
  navNode: NavigationNode
): CountryNavItem | null {
  const countryBarItem = navNode.entries[0]?.countryBar;

  if (!countryBarItem) {
    return null;
  }

  //CountryLanguageOptions are sorted to list the item with isSelected( set to true), to the top of the array
  return <CountryNavItem>{
    buttonLabel: countryBarItem.buttonLabel || "",
    mainText: countryBarItem.mainText || "",
    selectLocationLabel: countryBarItem.selectLocationLabel || "",
    countryLanguageOptions: countryBarItem.countryLanguageOptions
      .sort((x, y) => (x.isSelected ? 1 : 0) - (y.isSelected ? 1 : 0))
      .map(({ label, url, isSelected }) => ({ label, url, isSelected })),
    countryCode: country,
  };
}

const transformApiResponseToComponentData = (
  siteUid: string,
  apiResponse: CustomerNavigationBarApiResponse
): CustomerNavigationBarComponent => {
  const cnComponent: CustomerNavigationBarComponent = {
    countryLink: { link: undefined, text: undefined },
    helpLink: { link: undefined, text: undefined },
    dealerLink: { link: undefined, text: undefined },
    bikeRegistrationLink: { link: undefined, text: undefined },
    minicartLink: { link: undefined, text: undefined },
    productsCount: undefined,
    userName: undefined,
    accountLink: { link: undefined, text: undefined },
    accountOptions: [],
    checkoutLink: { link: undefined, text: undefined },
    viewcartLink: { link: undefined, text: undefined },
    totalLabel: undefined,
    totalValue: undefined,
    products: [],
  };
  apiResponse.navigationNode.children?.forEach((node) => {
    switch (node.uid) {
      case "country-flag-nav-node":
        cnComponent.countryLink = {
          text: (node.link
            ? node.link.split("/")[1]
            : siteUid.split("_")[1]
          )?.toUpperCase(),
          link: node.link,
        };
        break;
      case "help-nav-node": {
        const linkItem =
          node.entries?.find(
            (x) =>
              x.itemId === "HelpLinkTransactional" || "HelpLinkTransactional-US"
          ) || node.entries?.find((x) => x.itemId === "HelpLinkFallback");
        cnComponent.helpLink = {
          text: linkItem?.linkName
            ? removeAllHTMLTags(linkItem.linkName)
            : linkItem?.linkNameEN,
          link: linkItem?.link,
        };
        break;
      }
      case "dealer-locator-nav-node":
        cnComponent.dealerLink = {
          text: node.linkName ? removeAllHTMLTags(node.linkName) : node.titleEN,
          link: node.link,
        };
        break;
      case "bike-registration-nav-node":
        cnComponent.bikeRegistrationLink = {
          text: node.linkName ? removeAllHTMLTags(node.linkName) : node.titleEN,
          link: node.link,
        };
        break;
      case "mini-cart-nav-node":
        cnComponent.minicartLink = {
          text: node.linkName ? removeAllHTMLTags(node.linkName) : node.titleEN,
          link: node.link,
        };
        break;
      case "sign-in-nav-node":
        cnComponent.accountLink = {
          text: node.linkName ? removeAllHTMLTags(node.linkName) : node.titleEN,
          link: node.link,
        };
        break;
      default:
        break;
    }
  });
  return cnComponent;
};

export async function getSearchSuggestions(
  baseUrl: string,
  query: string
): Promise<ProductSearchBar[]> {
  const MIN_SEARCH_QUERY_LENGTH = 3;
  if (query.length < MIN_SEARCH_QUERY_LENGTH) {
    return [];
  }

  try {
    const response = await fetch(
      `${baseUrl}search/autocomplete/SearchBox?term=${query}`
    );
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    const data: ApiResponseSearchBar = await response.json();
    return data.mainProduct || [];
  } catch (_error) {
    return [];
  }
}
